import { computed, ref } from 'vue'

import type { LngLat } from '@/composables/useLocation'
import { useToastStore } from '@/stores/toast'
import type { AddressHint } from '@/utils/yandexSearch'
import { resolveHint, reverseGeocode, yandexSuggest } from '@/utils/yandexSearch'
import { t } from '@/i18n'

/** Пока адрес не определён — подпись из макета. */
/* Функция: подпись должна переезжать вместе с языком */
export const pointPlaceholder = (): string => t('errors.pointRequired')

/** Ниже трёх букв подсказки — мусор. */
const MIN_QUERY_LENGTH = 3
const SUGGEST_DEBOUNCE = 400
/**
 * Адрес под пином дёргается отдельно от координат: карту крутят чаще, чем
 * читают. Геокодер платный, и каждый лишний запрос приближает блокировку
 * ключа, поэтому ждём подольше — пока клиент не отпустит карту совсем.
 * Сверху ещё 150 мс своей задержки добавляет сама карта (`MapCanvas`).
 */
const ADDRESS_DEBOUNCE = 800

/**
 * Насколько надо сдвинуть пин, чтобы спрашивать адрес заново. Меньше — это
 * зум (центр вообще не меняется), дрожание пальца и доводка карты после
 * жеста: улица от такого не меняется, а запрос всё равно уходил.
 *
 * Этот же допуск решает, описывает ли известная подпись текущий пин: если
 * адрес не переспрашиваем, значит считаем его верным и для новой точки.
 */
const MIN_SHIFT_METERS = 15

const METERS_PER_DEGREE = 111_320

/** Плоская аппроксимация: на десятках метров разница с гаверсинусом ничтожна. */
function metersBetween(a: LngLat, b: LngLat): number {
  const dLat = (a[1] - b[1]) * METERS_PER_DEGREE
  const dLon = (a[0] - b[0]) * METERS_PER_DEGREE * Math.cos((b[1] * Math.PI) / 180)
  return Math.hypot(dLat, dLon)
}

/** ~1 м: карта возвращает свой центр округлённым, а руками так точно не попасть. */
const POINT_EPSILON = 1e-5

/**
 * Точка доставки: сдвиг карты, подсказки адреса и его расшифровка.
 * Единственный источник адресов — Яндекс: подсказка приносит строку сразу,
 * для точки, поставленной руками, работает обратное геокодирование.
 */
export function useMapPoint() {
  const $toast = useToastStore()

  const coords = ref<LngLat | null>(null)
  const address = ref('')
  /** Номер дома из разбора геокодера; у пина во дворе он пустой */
  const house = ref('')
  const query = ref('')
  const suggestions = ref<AddressHint[]>([])
  /**
   * Адрес для текущего пина ещё не получен: идёт пауза дебаунса или сам
   * запрос. Пока флаг поднят, показанная подпись относится к прошлой точке,
   * и отдавать её наружу нельзя.
   */
  const isAddressPending = ref(false)

  /** Точка, к которой относится текущее значение `address`. */
  const resolvedPoint = ref<LngLat | null>(null)

  const hasPoint = computed(() => coords.value !== null)
  const addressTitle = computed(() => address.value || pointPlaceholder())

  let suggestTimer: ReturnType<typeof setTimeout> | undefined
  let addressTimer: ReturnType<typeof setTimeout> | undefined
  /** Ответы приходят вразнобой — принимаем только последний запрос. */
  let lastAddressRequest = 0
  /** Адрес, пришедший вместе с подсказкой: он точнее обратного геокодирования. */
  let pinned: { point: LngLat; text: string; house: string } | null = null

  /** Карта после переезда возвращает свой центр — сравниваем с допуском. */
  function isSamePoint(a: LngLat, b: LngLat): boolean {
    return Math.abs(a[0] - b[0]) < POINT_EPSILON && Math.abs(a[1] - b[1]) < POINT_EPSILON
  }

  /**
   * Описывает ли известная подпись эту точку. Без этой проверки получалось
   * расхождение: клиент двигал пин и подтверждал адрес, не дождавшись нового
   * названия, — в справочник уезжали свежие координаты со старой улицей.
   */
  function isAddressFor(point: LngLat): boolean {
    const known = resolvedPoint.value
    return address.value !== '' && known !== null && metersBetween(point, known) < MIN_SHIFT_METERS
  }

  /** Сам запрос адреса: сюда приходят и дебаунс, и подтверждение точки. */
  async function requestAddress(point: LngLat): Promise<void> {
    pinned = null
    const request = ++lastAddressRequest
    isAddressPending.value = true
    const found = await reverseGeocode(point)

    /* Пин успели увести дальше — ответ уже не про него */
    if (request !== lastAddressRequest) return

    address.value = found?.address ?? ''
    house.value = found?.house ?? ''
    resolvedPoint.value = found ? point : null
    isAddressPending.value = false
  }

  /** Карту подвинули: координаты знаем сразу, адрес подтягиваем следом. */
  function moveTo(value: LngLat): void {
    coords.value = value
    clearTimeout(addressTimer)

    /* Это карта доехала до выбранной подсказки — её адрес и оставляем */
    if (pinned && isSamePoint(value, pinned.point)) {
      lastAddressRequest += 1
      address.value = pinned.text
      house.value = pinned.house
      resolvedPoint.value = value
      isAddressPending.value = false
      return
    }

    /* Пин почти не сдвинулся — показанный адрес и так верный */
    if (isAddressFor(value)) {
      isAddressPending.value = false
      return
    }

    /* Подпись уже не про этот пин: ждать её придётся и показу, и подтверждению */
    isAddressPending.value = true
    addressTimer = setTimeout(() => {
      /* Промежуточные кадры анимации сюда не долетают: их гасит clearTimeout */
      void requestAddress(value)
    }, ADDRESS_DEBOUNCE)
  }

  function search(text: string): void {
    query.value = text
    clearTimeout(suggestTimer)

    if (text.trim().length < MIN_QUERY_LENGTH) {
      suggestions.value = []
      return
    }

    suggestTimer = setTimeout(async () => {
      suggestions.value = await yandexSuggest(text.trim(), coords.value ?? undefined)
    }, SUGGEST_DEBOUNCE)
  }

  /**
   * Клиент выбрал подсказку. Координаты у неё не приходят — их отдаёт
   * Search API, и если у ключа нет к нему доступа, честно об этом говорим.
   */
  async function pick(hint: AddressHint): Promise<LngLat | null> {
    const place = await resolveHint(hint)
    if (!place) {
      $toast.warning(t('errors.pointNotFound'))
      return null
    }

    query.value = ''
    suggestions.value = []

    /* Адрес пришёл вместе с точкой — переспрашивать его по координатам незачем */
    pinned = { point: place.point, text: place.address, house: place.house }
    moveTo(place.point)
    return place.point
  }

  /**
   * Адрес перед подтверждением. Если подпись уже описывает этот пин, отдаём
   * её сразу; иначе не ждём остаток дебаунса, а спрашиваем геокодер прямо
   * сейчас — клиент стоит на кнопке, и экономить на этом запросе нечего.
   *
   * Отдаём адрес и дом вместе: порознь их легко разъехаться, а звонить
   * курьеру будут по одной записи.
   */
  async function resolveAddress(): Promise<{ address: string; house: string }> {
    const point = coords.value
    if (!point) return { address: '', house: '' }

    if (!isAddressFor(point)) {
      clearTimeout(addressTimer)
      await requestAddress(point)
    }

    /* Пин увели во время запроса — отданное геокодером уже не про него */
    if (!isAddressFor(point)) return { address: '', house: '' }

    return { address: address.value, house: house.value }
  }

  return {
    coords,
    address,
    house,
    addressTitle,
    query,
    suggestions,
    isAddressPending,
    hasPoint,
    moveTo,
    search,
    pick,
    resolveAddress,
  }
}
